import type { EventCategory } from '@orbit/api-types'
import { presentCard, type ChatCardGlyph } from './cardPresentation'
import { mentionText } from './composerDraft'
import type { ChatQuote } from './types'

export const DELETED_QUOTE_TEXT = 'Deleted message'

/**
 * What a quote says, in a reply's bubble and over the composer while replying: its
 * author, then the first card it tags or else its text. "Maya" over "✈ JL 61 · LAX → HND".
 * Ported from iOS `ChatQuotePresentation`.
 */
export interface ChatQuotePresentation {
	/** The quoted Message, to scroll to. */
	messageId: number
	/** "You" when I wrote it. */
	authorName: string
	/** I wrote it: the quote takes my accent, as a reply's does in WhatsApp. */
	isMine: boolean
	/** One line: a card's title, the text, or "Deleted message". */
	snippet: string
	/** The glyph of the card the snippet names; null for text. */
	glyph: ChatCardGlyph | null
	category: EventCategory | null
	isRemoved: boolean
}

export const presentQuote = (quote: ChatQuote, myUserId: number): ChatQuotePresentation => {
	const isMine = quote.author.id === myUserId
	const base = {
		messageId: quote.id,
		authorName: isMine ? 'You' : quote.author.firstName,
		isMine,
		isRemoved: quote.removed,
	}
	if (quote.removed) return { ...base, snippet: DELETED_QUOTE_TEXT, glyph: null, category: null }

	// A quoted reply's inherited cards name what it answered, not what it says, and
	// members read inline in the text: its text comes before either.
	const resources = quote.tags.filter(card => card.kind !== 'Member')
	const text =
		quote.text
			?.split(/\r\n|\n|\r|\u2028|\u2029/)
			.filter(line => line !== '')
			.join(' ') ?? null
	const card = resources.find(c => !c.inherited) ?? ((text ?? '') === '' ? resources[0] : undefined)
	if (card) {
		const presentation = presentCard(card)
		return { ...base, snippet: presentation.title, glyph: presentation.glyph, category: presentation.category }
	}
	const [first] = quote.tags
	return { ...base, snippet: text ?? (first ? mentionText(first) : ''), glyph: null, category: null }
}
